// src/pages/NewsDetailPage.js For Top Stories & Notification Taps
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import NewsCard from '../components/newsCard';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faThumbsUp, faThumbsDown, faShareAlt, faBookmark, 
    faComment, faExclamationTriangle, faUserCircle
} from '@fortawesome/free-solid-svg-icons';
import { getFirestore, doc, updateDoc, arrayUnion, arrayRemove, addDoc, collection } from "firebase/firestore";
import { app } from '../firebase-config';
import { Share } from '@capacitor/share';
import './NewsDetailPage.css';

const db = getFirestore(app);

const NewsDetailPage = ({ onReportSuccess, setActiveCommentPostId }) => {
    const { newsId } = useParams();
    const navigate = useNavigate();
    const { currentUser, firebaseUser } = useAuth();
    
    const [localNewsItem, setLocalNewsItem] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [currentImgIndex, setCurrentImgIndex] = useState(0);
    const [showReportOptions, setShowReportOptions] = useState(false);
    const [reportReason, setReportReason] = useState('');
    const [customReportText, setCustomReportText] = useState('');
    const [reportSubmitted, setReportSubmitted] = useState(false);

    const API_BASE_URL = 'https://api-5iacy4tfya-uc.a.run.app';

    const reportReasons = [
        "Fake news",
        "Mistakes in news",
        "Wrong or Bad words in the news",
        "Unfair/Biased Story",
        "Repeated Story"
    ];

    useEffect(() => {
        const fetchNews = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/news/${newsId}`);
                setLocalNewsItem(response.data);
            } catch (err) { setError("News not found."); }
            finally { setLoading(false); }
        };
        fetchNews();
    }, [newsId]);

    // Image Slider Logic (Auto-play every 3 seconds)
    useEffect(() => {
        if (!localNewsItem || !localNewsItem.imageUrls || localNewsItem.imageUrls.length <= 1) return;

        const interval = setInterval(() => {
            setCurrentImgIndex(prev => (prev + 1) % localNewsItem.imageUrls.length);
        }, 3000);

        return () => clearInterval(interval);
    }, [localNewsItem]);

    // Reaction Checks
    const hasLiked = useMemo(() => currentUser && localNewsItem?.likedBy?.includes(currentUser.uid), [currentUser, localNewsItem]);
    const hasDisliked = useMemo(() => currentUser && localNewsItem?.dislikedBy?.includes(currentUser.uid), [currentUser, localNewsItem]);
    const hasSaved = useMemo(() => currentUser && localNewsItem?.savedBy?.includes(currentUser.uid), [currentUser, localNewsItem]);

    // Formatters to prevent "Invalid Date"
    // Robust Timestamp Parser helper
    const parseTimestamp = (ts) => {
        if (!ts) return null;
        
        // 1. If it's a Firestore SDK Timestamp
        if (typeof ts.toDate === 'function') {
            return ts.toDate();
        }
        
        // 2. If it's a serialized JSON object from the REST API
        if (typeof ts === 'object') {
            if (typeof ts._seconds === 'number') {
                return new Date(ts._seconds * 1000);
            }
            if (typeof ts.seconds === 'number') {
                return new Date(ts.seconds * 1000);
            }
        }
        
        // 3. If it's an ISO string, string, or millisecond number
        const parsedDate = new Date(ts);
        return isNaN(parsedDate.getTime()) ? null : parsedDate;
    };

    // Formatters using the robust parser
    const getFormattedDate = (ts) => {
        const date = parseTimestamp(ts);
        return date ? date.toLocaleDateString('en-GB') : "Just now";
    };

    const getRelativeTime = (ts) => {
        const date = parseTimestamp(ts);
        if (!date) return "just now";

        const diffInSeconds = Math.floor((new Date() - date) / 1000);
        if (diffInSeconds < 60) return "just now";

        const diffInMinutes = Math.floor(diffInSeconds / 60);
        if (diffInMinutes < 60) {
            return `${diffInMinutes} min${diffInMinutes > 1 ? 's' : ''} ago`;
        }

        const diffInHours = Math.floor(diffInMinutes / 60);
        if (diffInHours < 24) {
            return `${diffInHours} hr${diffInHours > 1 ? 's' : ''} ago`;
        }

        const diffInDays = Math.floor(diffInHours / 24);
        if (diffInDays < 30) {
            return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
        }

        return date.toLocaleDateString('en-GB');
    };

    // LIKE / DISLIKE LOGIC (Same as NewsCard)
    const handleLikeDislike = async (action) => {
        if (!firebaseUser) { alert("Please log in."); return; }
        const originalItem = { ...localNewsItem };
        let updatedItem = { ...localNewsItem };
        const userId = firebaseUser.uid;

        if (action === 'like') {
            updatedItem.likedBy = hasLiked ? updatedItem.likedBy.filter(id => id !== userId) : [...updatedItem.likedBy, userId];
            updatedItem.dislikedBy = updatedItem.dislikedBy.filter(id => id !== userId);
        } else {
            updatedItem.dislikedBy = hasDisliked ? updatedItem.dislikedBy.filter(id => id !== userId) : [...updatedItem.dislikedBy, userId];
            updatedItem.likedBy = updatedItem.likedBy.filter(id => id !== userId);
        }
        setLocalNewsItem(updatedItem);

        try {
            const idToken = await firebaseUser.getIdToken(true);
            await axios.post(`${API_BASE_URL}/user-news/${localNewsItem.id}/action`, { action }, { headers: { Authorization: `Bearer ${idToken}` } });
        } catch (e) { setLocalNewsItem(originalItem); }
    };

    // SAVE LOGIC
    const handleSave = async () => {
        if (!firebaseUser || isSaving) return;
        setIsSaving(true);
        const userRef = doc(db, 'users', firebaseUser.uid);
        const newsRef = doc(db, 'userNews', localNewsItem.id);
        try {
            if (hasSaved) {
                await updateDoc(userRef, { savedPosts: arrayRemove(localNewsItem.id) });
                await updateDoc(newsRef, { savedBy: arrayRemove(firebaseUser.uid) });
                setLocalNewsItem(prev => ({ ...prev, savedBy: prev.savedBy.filter(id => id !== firebaseUser.uid) }));
            } else {
                await updateDoc(userRef, { savedPosts: arrayUnion(localNewsItem.id) });
                await updateDoc(newsRef, { savedBy: arrayUnion(firebaseUser.uid) });
                setLocalNewsItem(prev => ({ ...prev, savedBy: [...(prev.savedBy || []), firebaseUser.uid] }));
            }
        } catch (e) { console.error(e); }
        finally { setIsSaving(false); }
    };

    // SHARE LOGIC (The Deep Link Fix)
    const handleShare = async () => {
        const shareUrl = `https://ainewsapp.net/post/${localNewsItem.id}`; // CRITICAL: Use the domain that triggers the app
        const shareData = { title: localNewsItem.title, text: localNewsItem.title, url: shareUrl };
        try { await Share.share(shareData); } 
        catch (e) { navigator.clipboard.writeText(shareUrl); alert("Link copied!"); }
    };

    const handleReportClick = () => {
        setShowReportOptions(prev => !prev);
        setReportReason('');
        setCustomReportText('');
        setReportSubmitted(false);
    };

    // REPORT LOGIC
    const handleSubmitReport = async () => {
        if (!reportReason && !customReportText) { alert("Please select a reason."); return; }
        if (!firebaseUser) { alert("Please login to report."); return; }

        const reportData = {
            postId: localNewsItem.id || 'N/A',
            reporterId: firebaseUser.uid,
            reporterName: currentUser?.profile?.name || 'Anonymous',
            reporterEmail: firebaseUser.email || 'N/A',
            reporterMobile: currentUser?.profile?.phoneNumber || 'N/A',
            reason: reportReason || 'Other',
            customText: customReportText.trim(),
            reportedAt: new Date(),
            postTitle: localNewsItem.title || 'N/A',
            postAuthorId: localNewsItem.userId || 'N/A',
            postAuthorName: localNewsItem.userName || 'N/A',
            postAuthorMobile: localNewsItem.userContact || 'N/A',
            status: 'pending',
        };
        try {
            await addDoc(collection(db, 'reports'), reportData);
            setReportSubmitted(true);
            setTimeout(() => setShowReportOptions(false), 2000);
        } catch (error) { alert("Failed to submit report."); }
    };

    if (loading) return <div className="detail-status">Loading...</div>;
    if (!localNewsItem) return <div className="detail-status">News not found.</div>;

    return (
        <div className="news-detail-wrapper">
            <header className="detail-header-fixed">
                <button onClick={() => navigate('/')} className="back-btn">← Home</button>
                {/* <div className="header-branding">Ai News</div> */}
            </header>

            {/* Standard wrapper to constrain max-width, matching your feed style */}
            <div className="detail-card-container" style={{ maxWidth: '600px', margin: '15px auto', padding: '0 10px' }}>
                <NewsCard
                    newsItem={localNewsItem}
                    isFeedView={true} // Set to true to make it look EXACTLY like the feed card
                    setActiveCommentPostId={setActiveCommentPostId}
                />
            </div>
        </div>
    );
};

export default NewsDetailPage;